import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Bug, Lightbulb, Send, Loader2, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = ["open", "in_progress", "resolved", "closed"];

const STATUS_CLS = {
  open: "border-neutral-400 text-neutral-700",
  in_progress: "border-blue-500 text-blue-700",
  resolved: "border-emerald-600 text-emerald-700",
  closed: "border-neutral-300 text-neutral-500",
};

function TicketRow({ ticket, onUpdate }) {
  const { t } = useT();
  const [expanded, setExpanded] = useState(false);
  const [reply, setReply] = useState(ticket.admin_reply || "");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(ticket.status);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/tickets/${ticket.id}`, { status, admin_reply: reply });
      toast.success(t("plnUpdated"));
      onUpdate?.();
    } catch {
      toast.error(t("ticketAdmSaveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const Icon = ticket.kind === "bug" ? Bug : Lightbulb;

  return (
    <li className="border border-neutral-200 bg-white" data-testid={`admin-ticket-${ticket.id}`}>
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-neutral-50"
        data-testid={`admin-ticket-toggle-${ticket.id}`}
      >
        <Icon size={13} strokeWidth={1.5} className="mt-1 shrink-0 text-neutral-500" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-serif text-base truncate">{ticket.subject}</span>
            <span className={`shrink-0 font-mono text-[0.55rem] uppercase tracking-[0.12em] px-2 py-1 border ${STATUS_CLS[ticket.status]}`}>
              {t(`ticketStatus${ticket.status === "in_progress" ? "InProgress" : ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}`)}
            </span>
          </div>
          <p className="font-mono text-[0.6rem] text-neutral-500 mt-0.5">
            {ticket.user_email} · {new Date(ticket.created_at).toLocaleString()}
          </p>
        </div>
        {expanded ? <ChevronUp size={14} strokeWidth={1.5} /> : <ChevronDown size={14} strokeWidth={1.5} />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-neutral-100 pt-3 space-y-3">
          <div className="whitespace-pre-wrap text-sm leading-relaxed bg-neutral-50 border border-neutral-200 p-3">
            {ticket.description}
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
            <label className="block flex-1">
              <span className="ed-label mb-1 block">{t("ticketAdmStatus")}</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid={`admin-ticket-status-${ticket.id}`}
                className="ed-input"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {t(`ticketStatus${s === "in_progress" ? "InProgress" : s.charAt(0).toUpperCase() + s.slice(1)}`)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="ed-label mb-1 block">{t("ticketAdmReplyLabel")}</span>
            <textarea
              data-testid={`admin-ticket-reply-${ticket.id}`}
              className="ed-input min-h-[4.5rem]"
              value={reply}
              maxLength={4000}
              onChange={(e) => setReply(e.target.value)}
              placeholder={t("ticketAdmReplyPlaceholder")}
            />
          </label>

          <div className="flex justify-end">
            <button
              onClick={save}
              disabled={busy}
              data-testid={`admin-ticket-save-${ticket.id}`}
              className="inline-flex items-center gap-2 bg-neutral-900 text-[#F9F8F6] px-4 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] hover:bg-neutral-700 transition-colors disabled:opacity-50"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} strokeWidth={1.5} />}
              {t("ticketAdmSave")}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

export default function TicketsAdmin() {
  const { t } = useT();
  const [tickets, setTickets] = useState(null);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const [list, s] = await Promise.all([
        api.get("/admin/tickets", { params: filter === "all" ? {} : { status: filter } }),
        api.get("/admin/tickets/stats").catch(() => ({ data: null })),
      ]);
      setTickets(list.data);
      setStats(s?.data || null);
    } catch {
      toast.error(t("ticketAdmLoadFailed"));
    }
  }, [filter, t]);

  useEffect(() => { load(); }, [load]);

  const FILTERS = ["all", "open", "in_progress", "resolved", "closed"];

  return (
    <section className="mt-14" data-testid="tickets-admin">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <p className="ed-eyebrow">{t("ticketAdmEyebrow")}</p>
          <h2 className="font-serif text-2xl leading-tight mt-1">{t("ticketAdmTitle")}</h2>
        </div>
        <button data-testid="tickets-admin-refresh" onClick={load} className="p-2 border border-neutral-300 hover:border-neutral-900">
          <RefreshCw size={13} strokeWidth={1.5} />
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-5" data-testid="tickets-admin-stats">
          {["total", "open", "in_progress", "resolved", "bug", "suggestion"].map((k) => (
            <div key={k} className="ed-card p-3">
              <p className="font-mono text-[0.55rem] uppercase tracking-[0.12em] text-neutral-500">{t(`ticketStat${k.charAt(0).toUpperCase() + k.slice(1).replace(/_(.)/, (_, c) => c.toUpperCase())}`)}</p>
              <p className="font-serif text-2xl mt-1">{stats[k] || 0}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4" data-testid="tickets-admin-filters">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`tickets-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`font-mono text-[0.6rem] uppercase tracking-[0.12em] border px-3 py-1.5 transition-colors ${filter === f ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-300 hover:border-neutral-900"}`}
          >
            {f === "all" ? t("admFilterAll") : t(`ticketStatus${f === "in_progress" ? "InProgress" : f.charAt(0).toUpperCase() + f.slice(1)}`)}
          </button>
        ))}
      </div>

      {tickets === null ? (
        <p className="ed-eyebrow py-6">{t("loading")}</p>
      ) : tickets.length === 0 ? (
        <p className="text-sm text-neutral-500 italic font-serif py-4">{t("ticketAdmEmpty")}</p>
      ) : (
        <ul className="space-y-2">
          {tickets.map((tk) => (
            <TicketRow key={tk.id} ticket={tk} onUpdate={load} />
          ))}
        </ul>
      )}
    </section>
  );
}
